// Depth of field for a photo mode (decision 0060): three.js's bokeh pass put into a look's post chain, before bloom
// (after the scene and any passes the game gave), off until asked for. It draws the scene once more into a depth
// target while on, so a game turns it on only while it is wanted (photo mode, a cutscene). A look with no post chain
// (the low tier) gets none: `available` is false and the game records what it shows instead.
import type * as THREE from "three";
import { BokehPass } from "three/examples/jsm/postprocessing/BokehPass.js";
import type { Pass } from "three/examples/jsm/postprocessing/Pass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";

/** The post chain as the depth of field needs it (three's EffectComposer, or a stand-in in tests). */
export interface DofChain {
  passes: Pass[];
  insertPass(pass: Pass, index: number): void;
  removePass(pass: Pass): void;
}

/** A look's depth of field. */
export interface DepthOfField {
  /** False when the look has no post chain (nothing happens). */
  readonly available: boolean;
  /** On or off (off costs nothing). */
  enabled: boolean;
  /** Set the focus distance (m from the camera), the aperture (blur per metre out of focus) and the most blur. */
  set(o: { focus?: number; aperture?: number; maxBlur?: number }): void;
  /** What it is set to. */
  state(): { available: boolean; enabled: boolean; focus: number; aperture: number; maxBlur: number };
  /** Take the pass out of the chain. */
  dispose(): void;
}

/** Make the pass (separately, so a test can stand it in). */
export type DofPassFactory = (scene: THREE.Scene, camera: THREE.PerspectiveCamera, o: { focus: number; aperture: number; maxblur: number }) => Pass & { uniforms: Record<string, { value: unknown }> };

/** Put depth of field into `chain` (null: none on this tier) before its bloom pass, or before its last pass. */
export function createDepthOfField(o: {
  chain: DofChain | null;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  focus?: number;
  aperture?: number;
  maxBlur?: number;
  /** Which pass to go before (default the first named UnrealBloomPass, else the last). */
  before?: (p: Pass) => boolean;
  make?: DofPassFactory;
}): DepthOfField {
  const s = { focus: o.focus ?? 5, aperture: o.aperture ?? 0.005, maxBlur: o.maxBlur ?? 0.01 };
  if (!o.chain) {
    return {
      available: false,
      get enabled() {
        return false;
      },
      set enabled(_v: boolean) {},
      set: (v) => void Object.assign(s, Object.fromEntries(Object.entries(v).filter(([, x]) => x !== undefined))),
      state: () => ({ available: false, enabled: false, ...s }),
      dispose: () => {},
    };
  }
  const chain = o.chain;
  const make: DofPassFactory = o.make ?? ((scene, camera, p) => new BokehPass(scene, camera, p) as unknown as ReturnType<DofPassFactory>);
  const pass = make(o.scene, o.camera, { focus: s.focus, aperture: s.aperture, maxblur: s.maxBlur });
  pass.enabled = false;
  // (by class, and by name for a stand-in chain: a minified build renames classes, so the instance check comes first)
  const isBefore = o.before ?? ((p: Pass) => p instanceof UnrealBloomPass || p.constructor?.name === "UnrealBloomPass");
  let at = chain.passes.findIndex(isBefore);
  if (at < 0) at = Math.max(0, chain.passes.length - 1);
  chain.insertPass(pass, at);
  const write = (): void => {
    pass.uniforms.focus!.value = s.focus;
    pass.uniforms.aperture!.value = s.aperture;
    pass.uniforms.maxblur!.value = s.maxBlur;
  };
  write();
  return {
    available: true,
    get enabled() {
      return pass.enabled;
    },
    set enabled(v: boolean) {
      pass.enabled = v;
    },
    set(v) {
      if (v.focus !== undefined) s.focus = Math.max(0.01, v.focus);
      if (v.aperture !== undefined) s.aperture = Math.max(0, v.aperture);
      if (v.maxBlur !== undefined) s.maxBlur = Math.max(0, v.maxBlur);
      write();
    },
    state: () => ({ available: true, enabled: pass.enabled, ...s }),
    dispose: () => {
      chain.removePass(pass);
      pass.dispose();
    },
  };
}
